import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import normalizeDateString from "utils/common/normalizeDateString";

/**
 * Custom hook for managing duration state and calculations
 * @param {Object} params
 * @param {Object} params.location - React Router location object
 * @param {Array} params.moods - Array of mood objects
 * @param {string|Date} params.selectedDate - Selected date
 * @param {string} params.userId - Username
 * @param {string} params.feelingName - English mood label
 * @returns {Object} Duration management utilities
 */
export default function useDurationManagement({
  location,
  moods,
  selectedDate,
  userId,
  feelingName,
}) {
  const { t } = useTranslation();
  const [duration, setDuration] = useState(null);

  // Initialize duration from location state
  useEffect(() => {
    if (
      location?.state?.duration != null &&
      location?.state?.duration !== undefined
    ) {
      setDuration(location.state.duration);
    }
  }, [location?.state?.duration]);

  // Duration options with translations
  const durationOptions = useMemo(
    () => [
      { label: t("homepage.1-3"), duration: 1 },
      { label: t("homepage.3-6"), duration: 2 },
      { label: t("homepage.entireDay"), duration: 4 },
    ],
    [t]
  );

  // Calculate total duration used for the current day
  const getTotalDurationForToday = useMemo(() => {
    return () => {
      if (!moods || !selectedDate) return 0;

      const today = normalizeDateString(selectedDate);
      const todayMoods = moods.filter(
        (mood) =>
          mood.uid === userId && normalizeDateString(mood.timestamp) === today
      );

      return todayMoods.reduce(
        (total, mood) => total + (Number(mood.duration) || 0),
        0
      );
    };
  }, [moods, selectedDate, userId]);

  // Get previous duration for the current mood selection
  const getPreviousDurationForCurrentSelection = useMemo(() => {
    return () => {
      if (!moods || !selectedDate || !feelingName || !userId) return 0;
      const today = normalizeDateString(selectedDate);
      const entry = moods.find(
        (m) =>
          m.uid === userId &&
          m.mood === feelingName &&
          normalizeDateString(m.timestamp) === today
      );
      return Number(entry?.duration) || 0;
    };
  }, [moods, selectedDate, feelingName, userId]);

  // Get available duration options based on remaining capacity
  const getAvailableDurationOptions = useMemo(() => {
    return () => {
      const totalUsed = getTotalDurationForToday();
      const maxDailyDuration = 4;
      const previousDuration = getPreviousDurationForCurrentSelection();
      const effectiveUsed = Math.max(0, totalUsed - previousDuration);
      const remainingCapacity = maxDailyDuration - effectiveUsed;

      return durationOptions.filter(
        (option) => option.duration <= remainingCapacity
      );
    };
  }, [
    durationOptions,
    getTotalDurationForToday,
    getPreviousDurationForCurrentSelection,
  ]);

  // Check if a specific duration would exceed the daily limit
  const wouldExceedDailyLimit = useMemo(() => {
    return (durationValue, previousDurationOverride = null) => {
      const totalUsed = getTotalDurationForToday();
      const maxDailyDuration = 4;
      const previousDuration =
        previousDurationOverride != null
          ? Number(previousDurationOverride) || 0
          : getPreviousDurationForCurrentSelection();
      const effectiveUsed = Math.max(0, totalUsed - previousDuration);
      return effectiveUsed + (Number(durationValue) || 0) > maxDailyDuration;
    };
  }, [getTotalDurationForToday, getPreviousDurationForCurrentSelection]);

  // Get duration label for a given duration value
  const getDurationLabel = useMemo(() => {
    return (durationValue) => {
      const matchedOption = durationOptions.find(
        (option) => option.duration === durationValue
      );
      return matchedOption ? matchedOption.label : "";
    };
  }, [durationOptions]);

  return {
    duration,
    setDuration,
    durationOptions,
    getTotalDurationForToday,
    getPreviousDurationForCurrentSelection,
    getAvailableDurationOptions,
    wouldExceedDailyLimit,
    getDurationLabel,
  };
}
